import MaterialTable from '@mui/material/Table'; // Note that this implicitly renames the import from Table to MaterialTable to avoid naming conflicts below. The MUI component is NOT named MaterialTable.
import MaterialTableBody from '@mui/material/TableBody'; // Note that this implicitly renames the import from TableBody to MaterialTableBody to avoid naming conflicts below. The MUI component is NOT named MaterialTableBody.
import MaterialTableCell from '@mui/material/TableCell'; // Note that this implicitly renames the import from TableCell to MaterialTableCell to avoid naming conflicts below. The MUI component is NOT named MaterialTableCell.
import MaterialTableRow from '@mui/material/TableRow'; // Note that this implicitly renames the import from TableRow to MaterialTableRow to avoid naming conflicts below. The MUI component is NOT named MaterialTableRow.
import { type JSX, ReactNode } from 'react';

import styles from './MUITable.module.css';

export interface TableComponentWithChildren {
    children: ReactNode;
    className?: string;
}

/**
 * A simple table. Must have TableHead or TableBody as child.
 */
export function MUITable({ children }: TableComponentWithChildren): JSX.Element {
    return (
        <div className={styles.tableWrapper}>
            <MaterialTable>{children}</MaterialTable>
        </div>
    );
}

/**
 * A simple body for a table. Must have TableRow as child and Table as a parent.
 */
export function MUITableBody({ children }: TableComponentWithChildren): JSX.Element {
    return <MaterialTableBody sx={{ borderTop: 'none' }}>{children}</MaterialTableBody>;
}

/**
 * A simple row for a table. Must have a Table Cell as child and TableBody or TableHeader as a parent.
 */
export function MUITableRow({ children }: TableComponentWithChildren): JSX.Element {
    return <MaterialTableRow sx={{ lineHeight: '12px' }}>{children}</MaterialTableRow>;
}

const defaultTableCellStyle = { lineHeight: '12px', verticalAlign: 'center', padding: '.75rem' } as const;

/**
 * A simple cell for a Table. Must have TableRow as a parent. Anything can be its child.
 * @param {string?} className Can pass CSS from parent component to child component. CSS must be declared in Parent module.
 */
export function MUITableCell({ colSpan, className, children }: { colSpan?: number; className?: string; children?: React.ReactNode }): JSX.Element {
    return (
        <MaterialTableCell colSpan={colSpan} className={className ? className : undefined} sx={!className ? defaultTableCellStyle : {}}>
            {children}
        </MaterialTableCell>
    );
}

/**
 * A cell only meant to wrap an OverflowMenu Component. Use TableCell for any other component.
 */
export function MUITableOverflowCell({ className, children }: TableComponentWithChildren): JSX.Element {
    return (
        <MaterialTableCell className={className} sx={{ padding: '.25rem 0rem', verticalAlign: 'middle' }}>
            {children}
        </MaterialTableCell>
    );
}
